import { useLocale } from "@i18n/react";
import { useQueryClient } from "@tanstack/react-query";

import type { Collection } from "../../../lib/types/schema/collection/collectionSchema";
import type { Home } from "../../../lib/types/schema/home/homeSchema";
import type { SectionItem } from "../../../lib/types/schema/home/sectionItemSchema";
import type { Section } from "../../../lib/types/schema/home/sectionSchema";
import type { Pages } from "../../../lib/types/schema/pagesSchema";
import { useRequestMutation } from "../../../request/hooks/useRequestMutation";
import { keyCollection } from "../collection/keyCollection";
import { keyHome } from "../home/keyHome";
import { keyNextUp } from "../nextUp/keyNextUp";

export const useRequestChallenge = () => {
  const locale = useLocale();
  const queryClient = useQueryClient();

  const pagesQueries = queryClient.getQueriesData({
    predicate: (query) => {
      const key = query.queryKey;
      return Array.isArray(key[0]) && key[0][0] === "pages";
    },
  });

  const invalidateQueries = (challengeId: number) => {
    queryClient.invalidateQueries({ queryKey: [keyHome(), locale] });
    queryClient.invalidateQueries({ queryKey: [keyNextUp(), locale] });
    queryClient.invalidateQueries({
      queryKey: [keyCollection({ id: challengeId }), locale],
    });
    pagesQueries.forEach((query) => {
      queryClient.invalidateQueries({ queryKey: query[0] });
    });
  };

  return {
    mutationDelete: useRequestMutation({
      mutationFnRequestProps: ({ id }: { id: number }) => ({
        method: "DELETE",
        urlBackendPath: `/collections/${id}/users`,
      }),
      onSuccess: (data, { id }) => invalidateQueries(id),
    }),
    mutationPost: useRequestMutation({
      mutationFnRequestProps: ({ id }: { id: number }) => ({
        urlBackendPath: `/collections/${id}/users`,
      }),
      onMutate: async ({ id }: { id: number }) => {
        const challengeId = id;
        await queryClient.cancelQueries({
          queryKey: [keyCollection({ id: challengeId }), locale],
        });
        await queryClient.cancelQueries({ queryKey: [keyHome(), locale] });

        const previousCollection = queryClient.getQueryData([
          keyCollection({ id: challengeId }),
          locale,
        ]);
        const previousHome = queryClient.getQueryData([keyHome(), locale]);

        // Optimistically update collection to show user joined challenge
        queryClient.setQueryData(
          [keyCollection({ id: challengeId }), locale],
          (old: Collection) => {
            if (!old) return old;
            return {
              ...old,
              has_joined: true,
              users_count: (old.users_count || 0) + 1,
              place_in_line: (old.users_count || 0) + 1,
            };
          },
        );

        // Optimistically update home data if collection appears there
        queryClient.setQueryData([keyHome(), locale], (old: Home) => {
          if (!old?.sections) return old;
          return {
            ...old,
            sections: old.sections.map((section) => ({
              ...section,
              items: section.items?.map((item) => {
                if (item.reference?.collection?.id === challengeId) {
                  return {
                    ...item,
                    reference: {
                      ...item.reference,
                      collection: {
                        ...item.reference.collection,
                        has_joined: true,
                        users_count:
                          (item.reference.collection.users_count || 0) + 1,
                        place_in_line:
                          (item.reference.collection.users_count || 0) + 1,
                      },
                    },
                  };
                } else if (item.reference?.id === challengeId) {
                  return {
                    ...item,
                    reference: {
                      ...item.reference,
                      has_joined: true,
                      users_count: (item.reference.users_count || 0) + 1,
                      place_in_line: (item.reference.users_count || 0) + 1,
                    },
                  };
                }
                return item;
              }),
            })),
          };
        });

        pagesQueries.forEach((query) => {
          const queryKey = query[0];
          const currentData = queryClient.getQueryData(queryKey);

          if (!currentData) return;

          // Update the page data optimistically
          queryClient.setQueryData(queryKey, (oldData: Pages) => {
            if (!oldData?.sections) return oldData;

            return {
              ...oldData,
              sections: oldData.sections.map((section: Section) => ({
                ...section,
                items:
                  section.items?.map((item: SectionItem) => {
                    // Check if this item is the challenge being joined
                    if (
                      item.reference?.id === challengeId &&
                      item.reference?.type === "challenge"
                    ) {
                      return {
                        ...item,
                        reference: {
                          ...item.reference,
                          has_joined: true,
                          place_in_line: (item.reference.users_count || 0) + 1,
                          users_count: (item.reference.users_count || 0) + 1,
                        },
                      };
                    }
                    return item;
                  }) || [],
              })),
            };
          });
        });

        return { previousCollection, previousHome };
      },
      onError: (
        err,
        { id },
        context: { previousCollection: Collection; previousHome: Home },
      ) => {
        // Rollback optimistic updates
        if (context?.previousCollection) {
          queryClient.setQueryData(
            [keyCollection({ id }), locale],
            context.previousCollection,
          );
        }
        if (context?.previousHome) {
          queryClient.setQueryData([keyHome(), locale], context.previousHome);
        }
      },
      onSettled: (data, error, { id }) => invalidateQueries(id),
    }),
  };
};
